import { SPM_FIGURE_METADATA } from '../data/ipccData';

// Acoustic Pitch Anchor: 0.0°C = C4 (261.63 Hz)
// Lydian / Mixolydian / Dorian / Locrian Modal Scale Quantization Frequencies (Hz)
const SCALE_LYDIAN = [261.63, 293.66, 329.63, 369.99, 392.00, 440.00, 493.88, 523.25]; // C4 Lydian (0.0°C to +0.8°C)
const SCALE_MIXOLYDIAN = [261.63, 293.66, 329.63, 349.23, 392.00, 440.00, 466.16, 523.25]; // C4 Mixolydian (+0.8°C to +1.8°C)
const SCALE_DORIAN = [261.63, 293.66, 311.13, 349.23, 392.00, 440.00, 466.16, 523.25]; // C4 Dorian (+1.8°C to +2.8°C)
const SCALE_LOCRIAN = [261.63, 277.18, 311.13, 349.23, 369.99, 415.30, 466.16, 523.25]; // C4 Locrian (+2.8°C to +5.0°C)

export class AudioEngine {
  private static instance: AudioEngine | null = null;
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private masterLimiter: DynamicsCompressorNode | null = null;
  private tapeSaturationShaper: WaveShaperNode | null = null;

  // --- KARHIDE ETHEREAL SPATIAL DELAY & REVERB MATRIX ---
  private delayL: DelayNode | null = null;
  private delayR: DelayNode | null = null;
  private feedbackFilter: BiquadFilterNode | null = null;
  private feedbackGainL: GainNode | null = null;
  private feedbackGainR: GainNode | null = null;
  private reverbMasterGain: GainNode | null = null;

  // Track active movement index for transition flushing
  private activeMovementIdx: number = -1;

  // --- SUB-BASS HARMONIC GROUNDING GENERATOR ---
  private subBassOsc: OscillatorNode | null = null;
  private subBassGain: GainNode | null = null;

  private isMuted: boolean = false;
  private volume: number = 0.8;
  private isUnlocked: boolean = false;
  private isMusicalMode: boolean = true;
  private symphonyChannelGains: Map<string, number> = new Map();
  private symphonyMutedChannels: Set<string> = new Set();
  private symphonySoloChannel: string | null = null;

  // Active Synth Nodes
  private natWarmthOsc1: OscillatorNode | null = null;
  private natWarmthOsc2: OscillatorNode | null = null;
  private natWarmthLFO: OscillatorNode | null = null;
  private natWarmthGain: GainNode | null = null;

  private anthropoCarrier: OscillatorNode | null = null;
  private anthropoModulator: OscillatorNode | null = null;
  private anthropoModGain: GainNode | null = null;
  private anthropoFormantFilter: BiquadFilterNode | null = null;
  private anthropoGain: GainNode | null = null;

  private aerosolNoiseSource: AudioBufferSourceNode | null = null;
  private aerosolFilter1: BiquadFilterNode | null = null;
  private aerosolFilter2: BiquadFilterNode | null = null;
  private aerosolGain: GainNode | null = null;

  private cryoNoiseSource: AudioBufferSourceNode | null = null;
  private cryoFilter: BiquadFilterNode | null = null;
  private cryoShaper: WaveShaperNode | null = null;
  private cryoGain: GainNode | null = null;

  private abyssalOsc1: OscillatorNode | null = null;
  private abyssalOsc2: OscillatorNode | null = null;
  private abyssalShaper: WaveShaperNode | null = null;
  private abyssalGain: GainNode | null = null;

  private noiseBuffer: AudioBuffer | null = null;

  private constructor() {
    for (let i = 1; i <= 10; i++) {
      this.symphonyChannelGains.set(`spm${i}`, 0.8);
    }
  }

  public static getInstance(): AudioEngine {
    if (!AudioEngine.instance) {
      AudioEngine.instance = new AudioEngine();
    }
    return AudioEngine.instance;
  }

  public async unlock(): Promise<boolean> {
    if (!this.ctx) {
      const AudioCtxClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new AudioCtxClass();

      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.setValueAtTime(this.volume, this.ctx.currentTime);

      // --- MASTER BRICKWALL DYNAMICS COMPRESSOR / LIMITER ---
      this.masterLimiter = this.ctx.createDynamicsCompressor();
      this.masterLimiter.threshold.setValueAtTime(-12, this.ctx.currentTime); // Clamp peaks at -12 dBFS
      this.masterLimiter.knee.setValueAtTime(4, this.ctx.currentTime);
      this.masterLimiter.ratio.setValueAtTime(16.0, this.ctx.currentTime); // Brickwall peak limiting
      this.masterLimiter.attack.setValueAtTime(0.003, this.ctx.currentTime); // Fast 3ms attack
      this.masterLimiter.release.setValueAtTime(0.15, this.ctx.currentTime); // Smooth release

      // --- 1. TAPE SATURATION WAVESHAPER ---
      this.tapeSaturationShaper = this.ctx.createWaveShaper();
      this.tapeSaturationShaper.curve = this.makeDistortionCurve(12) as any;

      // --- 2. KARHIDE DUAL STEREO MULTI-TAP DELAY MATRIX ---
      this.delayL = this.ctx.createDelay(2.5);
      this.delayR = this.ctx.createDelay(2.5);

      this.delayL.delayTime.setValueAtTime(0.48, this.ctx.currentTime);
      this.delayR.delayTime.setValueAtTime(0.72, this.ctx.currentTime);

      this.feedbackFilter = this.ctx.createBiquadFilter();
      this.feedbackFilter.type = 'lowpass';
      this.feedbackFilter.frequency.setValueAtTime(1800, this.ctx.currentTime);

      this.feedbackGainL = this.ctx.createGain();
      this.feedbackGainR = this.ctx.createGain();
      this.feedbackGainL.gain.setValueAtTime(0.35, this.ctx.currentTime);
      this.feedbackGainR.gain.setValueAtTime(0.35, this.ctx.currentTime);

      this.reverbMasterGain = this.ctx.createGain();
      this.reverbMasterGain.gain.setValueAtTime(0.30, this.ctx.currentTime);

      const pannerL = this.ctx.createStereoPanner ? this.ctx.createStereoPanner() : null;
      const pannerR = this.ctx.createStereoPanner ? this.ctx.createStereoPanner() : null;

      if (pannerL && pannerR) {
        pannerL.pan.setValueAtTime(-0.7, this.ctx.currentTime);
        pannerR.pan.setValueAtTime(0.7, this.ctx.currentTime);

        this.masterGain.connect(this.delayL);
        this.masterGain.connect(this.delayR);

        this.delayL.connect(this.feedbackFilter);
        this.delayR.connect(this.feedbackFilter);

        this.feedbackFilter.connect(this.feedbackGainL);
        this.feedbackFilter.connect(this.feedbackGainR);

        this.feedbackGainL.connect(this.delayR);
        this.feedbackGainR.connect(this.delayL);

        this.delayL.connect(pannerL);
        this.delayR.connect(pannerR);

        pannerL.connect(this.reverbMasterGain);
        pannerR.connect(this.reverbMasterGain);
      } else {
        this.masterGain.connect(this.delayL);
        this.delayL.connect(this.reverbMasterGain);
      }

      // --- 3. SUB-BASS HARMONIC GROUNDING GENERATOR ---
      this.subBassOsc = this.ctx.createOscillator();
      this.subBassGain = this.ctx.createGain();
      this.subBassOsc.type = 'sine';
      this.subBassOsc.frequency.setValueAtTime(36, this.ctx.currentTime);
      this.subBassGain.gain.setValueAtTime(0.2 * this.volume, this.ctx.currentTime);

      this.subBassOsc.connect(this.subBassGain);
      this.subBassGain.connect(this.masterGain);
      this.subBassOsc.start();

      // Master Signal Chain: MasterGain -> Tape Saturation -> Master Limiter -> Destination
      this.masterGain.connect(this.tapeSaturationShaper);
      this.tapeSaturationShaper.connect(this.masterLimiter);
      this.reverbMasterGain.connect(this.masterLimiter);
      this.masterLimiter.connect(this.ctx.destination);

      this.generateNoiseBuffer();
    }

    if (this.ctx.state === 'suspended') {
      await this.ctx.resume();
    }

    this.isUnlocked = this.ctx.state === 'running';
    return this.isUnlocked;
  }

  public getIsUnlocked(): boolean {
    return this.isUnlocked && this.ctx !== null && this.ctx.state === 'running';
  }

  public setMusicalMode(enabled: boolean) {
    this.isMusicalMode = enabled;
  }

  public getIsMusicalMode(): boolean {
    return this.isMusicalMode;
  }

  public toggleMusicalMode(): boolean {
    this.isMusicalMode = !this.isMusicalMode;
    return this.isMusicalMode;
  }

  public setMasterVolume(val: number) {
    this.volume = Math.max(0, Math.min(1, val));
    if (this.ctx && this.masterGain && !this.isMuted) {
      this.masterGain.gain.setValueAtTime(this.volume, this.ctx.currentTime);
    }
  }

  public getMasterVolume(): number {
    return this.volume;
  }

  public toggleMute(): boolean {
    this.isMuted = !this.isMuted;
    if (this.ctx && this.masterGain) {
      const targetGain = this.isMuted ? 0 : this.volume;
      this.masterGain.gain.setValueAtTime(targetGain, this.ctx.currentTime);
    }
    return this.isMuted;
  }

  public getIsMuted(): boolean {
    return this.isMuted;
  }

  public playReferenceTone(duration: number = 1.5) {
    if (!this.ctx || !this.masterGain) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(261.63, this.ctx.currentTime);

    gain.gain.setValueAtTime(0, this.ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0.3 * this.volume, this.ctx.currentTime + 0.1);
    gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + duration);

    osc.connect(gain);
    gain.connect(this.masterGain);
    osc.start(this.ctx.currentTime);
    osc.stop(this.ctx.currentTime + duration);
  }

  public startCDRSweep(cdrVolume: number = 5) {
    if (!this.ctx || !this.masterGain) return;
    const cdrFreq = Math.max(25, 80 - cdrVolume * 3);
    this.startAbyssalDrone(cdrFreq, 0.45);
  }

  public stopCDRSweep() {
    this.stopAbyssalDrone();
  }

  public playSDGInterval(score: number) {
    if (!this.ctx || !this.masterGain) return;
    const root = 440;
    let ratios: number[] = [1.0, 1.25, 1.5, 1.875];
    if (score < 0) ratios = [1.0, 1.189, 1.414, 1.681];

    ratios.forEach((r, idx) => {
      if (!this.ctx || !this.masterGain) return;
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();

      osc.type = score >= 0 ? 'sine' : 'sawtooth';
      osc.frequency.setValueAtTime(root * r, this.ctx.currentTime);

      gain.gain.setValueAtTime(0.2 * this.volume, this.ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 0.8);

      const panner = this.ctx.createStereoPanner ? this.ctx.createStereoPanner() : null;
      if (panner) {
        panner.pan.setValueAtTime((idx / 3) * 1.2 - 0.6, this.ctx.currentTime);
        osc.connect(panner);
        panner.connect(gain);
      } else {
        osc.connect(gain);
      }

      gain.connect(this.masterGain);
      osc.start(this.ctx.currentTime);
      osc.stop(this.ctx.currentTime + 0.8);
    });
  }

  public getChannelGain(figId: string): number {
    return this.symphonyChannelGains.get(figId) ?? 0.8;
  }

  public setChannelGain(figId: string, gain: number) {
    this.symphonyChannelGains.set(figId, Math.max(0, Math.min(1, gain)));
  }

  public toggleChannelMute(figId: string): boolean {
    if (this.symphonyMutedChannels.has(figId)) {
      this.symphonyMutedChannels.delete(figId);
      return false;
    } else {
      this.symphonyMutedChannels.add(figId);
      return true;
    }
  }

  public isChannelMuted(figId: string): boolean {
    return this.symphonyMutedChannels.has(figId);
  }

  public setSoloChannel(figId: string | null) {
    this.symphonySoloChannel = figId;
  }

  public getSoloChannel(): string | null {
    return this.symphonySoloChannel;
  }

  public tempToFreq(tempAnomaly: number): number {
    const clampedTemp = Math.max(-2, Math.min(6, tempAnomaly));

    if (!this.isMusicalMode) {
      const semitones = clampedTemp * 4.8;
      return 261.63 * Math.pow(2, semitones / 12);
    }

    let targetScale = SCALE_LYDIAN;
    if (clampedTemp >= 2.8) targetScale = SCALE_LOCRIAN;
    else if (clampedTemp >= 1.8) targetScale = SCALE_DORIAN;
    else if (clampedTemp >= 0.8) targetScale = SCALE_MIXOLYDIAN;

    const normIndex = Math.round((Math.max(0, clampedTemp) / 5.0) * (targetScale.length - 1));
    return targetScale[Math.min(targetScale.length - 1, normIndex)];
  }

  public playSymphonyMovement(movementIdx: number, progress: number) {
    if (this.isMuted || !this.ctx) return;

    // Movement Transition Flush & Reverb Dampening (Seamless 300ms Crossfade)
    if (this.activeMovementIdx !== movementIdx) {
      this.activeMovementIdx = movementIdx;
      this.fadeoutAll(0.3); // Smoothly ramp down old synths over 300ms (zero pops)

      // Smoothly duck feedback delay gain without instantaneous jumps
      if (this.ctx && this.feedbackGainL && this.feedbackGainR) {
        const now = this.ctx.currentTime;
        this.feedbackGainL.gain.cancelScheduledValues(now);
        this.feedbackGainR.gain.cancelScheduledValues(now);
        this.feedbackGainL.gain.linearRampToValueAtTime(0.08, now + 0.15);
        this.feedbackGainR.gain.linearRampToValueAtTime(0.08, now + 0.15);
        this.feedbackGainL.gain.linearRampToValueAtTime(0.35, now + 0.6);
        this.feedbackGainR.gain.linearRampToValueAtTime(0.35, now + 0.6);
      }
    }

    // Movement 0: SPM.1 & SPM.2 (The Human Signal)
    if (movementIdx === 0) {
      const temp = -0.2 + progress * 1.5;
      const freq = this.tempToFreq(temp);
      this.startNatWarmth(freq, 0.22 * (this.symphonyChannelGains.get('spm1') ?? 0.8));
      if (progress > 0.3) {
        this.startAnthropoPulse(freq * 1.25, 2.0, 0.18 * (this.symphonyChannelGains.get('spm2') ?? 0.8));
      }
    }
    // Movement 1 (Movement II): SPM.3 & SPM.9 (Regional Extremes & Risk Matrix)
    else if (movementIdx === 1) {
      // Replaced beeping transients with a lush, wide regional ambient swell
      const baseFreq = 196.00; // G3 Warm Regional Bass Anchor
      const tempFreq = this.tempToFreq(1.2 + progress * 0.4);

      // Continuous Bowed Ambient Pad for Regional Extremes
      this.startNatWarmth(tempFreq, 0.20 * (this.symphonyChannelGains.get('spm3') ?? 0.8));

      // Organic Atmospheric Wind/Swell for Risk Matrix
      const windCutoff = 220 + Math.sin(progress * Math.PI * 2) * 120;
      this.startAerosolShield(windCutoff, 1.8, 0.10 * (this.symphonyChannelGains.get('spm9') ?? 0.8));
    }
    // Movement 2: SPM.4 & SPM.5 (Future Scenarios & Spatial Warming)
    else if (movementIdx === 2) {
      const freq1 = this.tempToFreq(1.2 + progress * 0.8);
      const freq2 = this.tempToFreq(1.8 + progress * 2.0);
      this.startNatWarmth(freq1, 0.22 * (this.symphonyChannelGains.get('spm4') ?? 0.8));
      this.startAnthropoPulse(freq2, 3.0, 0.20 * (this.symphonyChannelGains.get('spm5') ?? 0.8));
    }
    // Movement 3: SPM.6 & SPM.7 (Carbon Metronome & Frequency Accelerations)
    else if (movementIdx === 3) {
      const freq = this.tempToFreq(progress * 2.8);
      this.triggerExtremeTransient(freq, 0, 0.35 * (this.symphonyChannelGains.get('spm7') ?? 0.8));
      this.startAerosolShield(160 + progress * 500, 3, 0.15 * (this.symphonyChannelGains.get('spm6') ?? 0.8));
    }
    // Movement 4: SPM.8 & SPM.10 (Multi-Century Commitment & Carbon Budget)
    else if (movementIdx === 4) {
      const droneFreq = 26 + progress * 30;
      this.startAbyssalDrone(droneFreq, 0.30 * (this.symphonyChannelGains.get('spm8') ?? 0.8));
      this.startCryoHiss(5000 - progress * 4000, 0.12 * (this.symphonyChannelGains.get('spm10') ?? 0.8));
    }

    // Update Sub-Bass Grounding Generator Frequency
    if (this.subBassOsc && this.subBassGain) {
      const subFreq = 32 + progress * 15;
      this.subBassOsc.frequency.setTargetAtTime(subFreq, this.ctx.currentTime, 0.15);
    }
  }

  // --- KARHIDE BOWING ATTACK SYNTH PATCHES ---
  public startNatWarmth(freq: number, gainLevel: number = 0.25) {
    if (!this.ctx || !this.masterGain) return;
    this.stopNatWarmth();

    this.natWarmthGain = this.ctx.createGain();
    this.natWarmthGain.gain.setValueAtTime(0.001, this.ctx.currentTime);
    // Soft bowing attack (600ms swell)
    this.natWarmthGain.gain.exponentialRampToValueAtTime(gainLevel * this.volume, this.ctx.currentTime + 0.6);

    this.natWarmthOsc1 = this.ctx.createOscillator();
    this.natWarmthOsc2 = this.ctx.createOscillator();
    this.natWarmthLFO = this.ctx.createOscillator();

    this.natWarmthOsc1.type = 'triangle';
    this.natWarmthOsc2.type = 'sine';
    this.natWarmthLFO.type = 'sine';

    this.natWarmthOsc1.frequency.setValueAtTime(freq, this.ctx.currentTime);
    this.natWarmthOsc2.frequency.setValueAtTime(freq * 1.002, this.ctx.currentTime);
    this.natWarmthLFO.frequency.setValueAtTime(0.05, this.ctx.currentTime); // Ultra-slow LFO

    const lfoGain = this.ctx.createGain();
    lfoGain.gain.setValueAtTime(2, this.ctx.currentTime);
    this.natWarmthLFO.connect(lfoGain);
    lfoGain.connect(this.natWarmthOsc1.frequency);

    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(Math.min(freq * 2.5, 1800), this.ctx.currentTime);
    filter.Q.setValueAtTime(1.2, this.ctx.currentTime);

    this.natWarmthOsc1.connect(filter);
    this.natWarmthOsc2.connect(filter);
    filter.connect(this.natWarmthGain);
    this.natWarmthGain.connect(this.masterGain);

    this.natWarmthOsc1.start();
    this.natWarmthOsc2.start();
    this.natWarmthLFO.start();
  }

  public updateNatWarmth(freq: number, gainLevel: number = 0.25) {
    if (this.ctx && this.natWarmthOsc1 && this.natWarmthOsc2 && this.natWarmthGain) {
      // Smooth microtonal pitch glide (150ms time constant)
      this.natWarmthOsc1.frequency.setTargetAtTime(freq, this.ctx.currentTime, 0.15);
      this.natWarmthOsc2.frequency.setTargetAtTime(freq * 1.002, this.ctx.currentTime, 0.15);
      this.natWarmthGain.gain.setTargetAtTime(gainLevel * this.volume, this.ctx.currentTime, 0.15);
    }
  }

  public stopNatWarmth() {
    if (this.natWarmthOsc1) { try { this.natWarmthOsc1.stop(); } catch {} this.natWarmthOsc1 = null; }
    if (this.natWarmthOsc2) { try { this.natWarmthOsc2.stop(); } catch {} this.natWarmthOsc2 = null; }
    if (this.natWarmthLFO) { try { this.natWarmthLFO.stop(); } catch {} this.natWarmthLFO = null; }
    this.natWarmthGain = null;
  }

  public startAnthropoPulse(freq: number, fmIndex: number = 2.5, gainLevel: number = 0.20) {
    if (!this.ctx || !this.masterGain) return;
    this.stopAnthropoPulse();

    this.anthropoGain = this.ctx.createGain();
    this.anthropoGain.gain.setValueAtTime(0.001, this.ctx.currentTime);
    this.anthropoGain.gain.exponentialRampToValueAtTime(gainLevel * this.volume, this.ctx.currentTime + 0.8);

    this.anthropoCarrier = this.ctx.createOscillator();
    this.anthropoModulator = this.ctx.createOscillator();
    this.anthropoModGain = this.ctx.createGain();
    this.anthropoFormantFilter = this.ctx.createBiquadFilter();

    this.anthropoCarrier.type = 'sawtooth';
    this.anthropoModulator.type = 'sine';

    const modRatio = 1.414;
    this.anthropoCarrier.frequency.setValueAtTime(freq, this.ctx.currentTime);
    this.anthropoModulator.frequency.setValueAtTime(freq * modRatio, this.ctx.currentTime);
    this.anthropoModGain.gain.setValueAtTime(freq * fmIndex, this.ctx.currentTime);

    this.anthropoModulator.connect(this.anthropoModGain);
    this.anthropoModGain.connect(this.anthropoCarrier.frequency);

    this.anthropoFormantFilter.type = 'bandpass';
    this.anthropoFormantFilter.frequency.setValueAtTime(Math.min(8000, freq * (1 + fmIndex * 0.4)), this.ctx.currentTime);
    this.anthropoFormantFilter.Q.setValueAtTime(2.5, this.ctx.currentTime);

    this.anthropoCarrier.connect(this.anthropoFormantFilter);
    this.anthropoFormantFilter.connect(this.anthropoGain);
    this.anthropoGain.connect(this.masterGain);

    this.anthropoCarrier.start();
    this.anthropoModulator.start();
  }

  public stopAnthropoPulse() {
    if (this.anthropoCarrier) { try { this.anthropoCarrier.stop(); } catch {} this.anthropoCarrier = null; }
    if (this.anthropoModulator) { try { this.anthropoModulator.stop(); } catch {} this.anthropoModulator = null; }
    this.anthropoGain = null;
  }

  public startAerosolShield(centerFreq: number = 300, Q: number = 2, gainLevel: number = 0.15) {
    if (!this.ctx || !this.masterGain || !this.noiseBuffer) return;
    this.stopAerosolShield();

    this.aerosolNoiseSource = this.ctx.createBufferSource();
    this.aerosolNoiseSource.buffer = this.noiseBuffer;
    this.aerosolNoiseSource.loop = true;

    this.aerosolFilter1 = this.ctx.createBiquadFilter();
    this.aerosolFilter2 = this.ctx.createBiquadFilter();

    this.aerosolFilter1.type = 'bandpass';
    this.aerosolFilter2.type = 'bandpass';

    this.aerosolFilter1.frequency.setValueAtTime(centerFreq, this.ctx.currentTime);
    this.aerosolFilter2.frequency.setValueAtTime(centerFreq * 1.5, this.ctx.currentTime);

    this.aerosolGain = this.ctx.createGain();
    this.aerosolGain.gain.setValueAtTime(0.001, this.ctx.currentTime);
    this.aerosolGain.gain.exponentialRampToValueAtTime(gainLevel * this.volume, this.ctx.currentTime + 1.0);

    this.aerosolNoiseSource.connect(this.aerosolFilter1);
    this.aerosolNoiseSource.connect(this.aerosolFilter2);

    this.aerosolFilter1.connect(this.aerosolGain);
    this.aerosolFilter2.connect(this.aerosolGain);
    this.aerosolGain.connect(this.masterGain);

    this.aerosolNoiseSource.start();
  }

  public stopAerosolShield() {
    if (this.aerosolNoiseSource) { try { this.aerosolNoiseSource.stop(); } catch {} this.aerosolNoiseSource = null; }
    this.aerosolGain = null;
  }

  public startCryoHiss(highCutoff: number = 6000, gainLevel: number = 0.12) {
    if (!this.ctx || !this.masterGain || !this.noiseBuffer) return;
    this.stopCryoHiss();

    this.cryoNoiseSource = this.ctx.createBufferSource();
    this.cryoNoiseSource.buffer = this.noiseBuffer;
    this.cryoNoiseSource.loop = true;

    this.cryoFilter = this.ctx.createBiquadFilter();
    this.cryoFilter.type = 'highpass';
    this.cryoFilter.frequency.setValueAtTime(highCutoff, this.ctx.currentTime);

    this.cryoShaper = this.ctx.createWaveShaper();
    this.cryoShaper.curve = this.makeBitCrushCurve(6) as any;

    this.cryoGain = this.ctx.createGain();
    this.cryoGain.gain.setValueAtTime(0.001, this.ctx.currentTime);
    this.cryoGain.gain.exponentialRampToValueAtTime(gainLevel * this.volume, this.ctx.currentTime + 1.2);

    this.cryoNoiseSource.connect(this.cryoFilter);
    this.cryoFilter.connect(this.cryoShaper);
    this.cryoShaper.connect(this.cryoGain);
    this.cryoGain.connect(this.masterGain);

    this.cryoNoiseSource.start();
  }

  public stopCryoHiss() {
    if (this.cryoNoiseSource) { try { this.cryoNoiseSource.stop(); } catch {} this.cryoNoiseSource = null; }
    this.cryoGain = null;
  }

  public startAbyssalDrone(freq: number = 30, gainLevel: number = 0.30) {
    if (!this.ctx || !this.masterGain) return;
    this.stopAbyssalDrone();

    this.abyssalGain = this.ctx.createGain();
    this.abyssalGain.gain.setValueAtTime(0.001, this.ctx.currentTime);
    this.abyssalGain.gain.exponentialRampToValueAtTime(gainLevel * this.volume, this.ctx.currentTime + 1.5);

    this.abyssalOsc1 = this.ctx.createOscillator();
    this.abyssalOsc2 = this.ctx.createOscillator();

    this.abyssalOsc1.type = 'sine';
    this.abyssalOsc2.type = 'triangle';

    this.abyssalOsc1.frequency.setValueAtTime(freq, this.ctx.currentTime);
    this.abyssalOsc2.frequency.setValueAtTime(freq + 1.2, this.ctx.currentTime);

    this.abyssalShaper = this.ctx.createWaveShaper();
    this.abyssalShaper.curve = this.makeDistortionCurve(10) as any;

    this.abyssalOsc1.connect(this.abyssalShaper);
    this.abyssalOsc2.connect(this.abyssalShaper);
    this.abyssalShaper.connect(this.abyssalGain);
    this.abyssalGain.connect(this.masterGain);

    this.abyssalOsc1.start();
    this.abyssalOsc2.start();
  }

  public stopAbyssalDrone() {
    if (this.abyssalOsc1) { try { this.abyssalOsc1.stop(); } catch {} this.abyssalOsc1 = null; }
    if (this.abyssalOsc2) { try { this.abyssalOsc2.stop(); } catch {} this.abyssalOsc2 = null; }
    this.abyssalGain = null;
  }

  public triggerExtremeTransient(pitch: number = 440, pan: number = 0, intensity: number = 0.25) {
    if (!this.ctx || !this.masterGain) return;

    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc.type = 'sine'; // Soft, pure sine chime timbre
    osc.frequency.setValueAtTime(pitch, this.ctx.currentTime);

    gain.gain.setValueAtTime(0.001, this.ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(intensity * 0.3 * this.volume, this.ctx.currentTime + 0.15);
    gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 1.2);

    const panner = this.ctx.createStereoPanner ? this.ctx.createStereoPanner() : null;
    if (panner) {
      panner.pan.setValueAtTime(pan, this.ctx.currentTime);
      osc.connect(panner);
      panner.connect(gain);
    } else {
      osc.connect(gain);
    }

    gain.connect(this.masterGain);
    osc.start(this.ctx.currentTime);
    osc.stop(this.ctx.currentTime + 1.2);
  }

  public playWarningChime(freq: number = 392.00, tempLevel: number = 1.0) {
    if (!this.ctx || !this.masterGain) return;

    // Graduated acoustic chime structure based on threshold criticality
    let freqs = [freq];
    let gains = [0.08];

    if (tempLevel >= 4.0) {
      freqs = [freq, freq * 1.5, freq * 2.76];
      gains = [0.30, 0.20, 0.12];
    } else if (tempLevel >= 2.0) {
      freqs = [freq, freq * 1.414, freq * 2.0];
      gains = [0.22, 0.14, 0.08];
    } else if (tempLevel >= 1.5) {
      freqs = [freq, freq * 1.25];
      gains = [0.14, 0.08];
    }

    freqs.forEach((f, i) => {
      if (!this.ctx || !this.masterGain) return;
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(f, this.ctx.currentTime);

      gain.gain.setValueAtTime(0.001, this.ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(gains[i] * this.volume, this.ctx.currentTime + 0.3);
      gain.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 2.5);

      osc.connect(gain);
      gain.connect(this.masterGain);

      osc.start(this.ctx.currentTime);
      osc.stop(this.ctx.currentTime + 2.5);
    });
  }

  private generateNoiseBuffer() {
    if (!this.ctx) return;
    const bufferSize = this.ctx.sampleRate * 2;
    const buffer = this.ctx.createBuffer(2, bufferSize, this.ctx.sampleRate);
    const left = buffer.getChannelData(0);
    const right = buffer.getChannelData(1);

    let b0L = 0, b1L = 0, b2L = 0, b3L = 0, b4L = 0, b5L = 0, b6L = 0;
    let b0R = 0, b1R = 0, b2R = 0, b3R = 0, b4R = 0, b5R = 0, b6R = 0;

    for (let i = 0; i < bufferSize; i++) {
      const whiteL = Math.random() * 2 - 1;
      b0L = 0.99886 * b0L + whiteL * 0.0555179;
      b1L = 0.99332 * b1L + whiteL * 0.0750759;
      b2L = 0.96900 * b2L + whiteL * 0.1538520;
      b3L = 0.86650 * b3L + whiteL * 0.3104856;
      b4L = 0.55000 * b4L + whiteL * 0.5329522;
      b5L = -0.7616 * b5L - whiteL * 0.0168980;
      left[i] = (b0L + b1L + b2L + b3L + b4L + b5L + b6L + whiteL * 0.5362) * 0.08;
      b6L = whiteL * 0.115926;

      const whiteR = Math.random() * 2 - 1;
      b0R = 0.99886 * b0R + whiteR * 0.0555179;
      b1R = 0.99332 * b1R + whiteR * 0.0750759;
      b2R = 0.96900 * b2R + whiteR * 0.1538520;
      b3R = 0.86650 * b3R + whiteR * 0.3104856;
      b4R = 0.55000 * b4R + whiteR * 0.5329522;
      b5R = -0.7616 * b5R - whiteR * 0.0168980;
      right[i] = (b0R + b1R + b2R + b3R + b4R + b5R + b6R + whiteR * 0.5362) * 0.08;
      b6R = whiteR * 0.115926;
    }
    this.noiseBuffer = buffer;
  }

  private makeDistortionCurve(amount: number = 12): Float32Array {
    const k = amount;
    const n_samples = 44100;
    const curve = new Float32Array(n_samples);
    const deg = Math.PI / 180;
    for (let i = 0; i < n_samples; ++i) {
      const x = (i * 2) / n_samples - 1;
      curve[i] = ((3 + k) * x * 20 * deg) / (Math.PI + k * Math.abs(x));
    }
    return curve as unknown as Float32Array;
  }

  private makeBitCrushCurve(bits: number = 6): Float32Array {
    const n_samples = 44100;
    const curve = new Float32Array(n_samples);
    const step = Math.pow(0.5, bits);
    for (let i = 0; i < n_samples; ++i) {
      const x = (i * 2) / n_samples - 1;
      curve[i] = Math.round(x / step) * step;
    }
    return curve as unknown as Float32Array;
  }

  public fadeoutAll(durationSeconds: number = 0.3) {
    if (!this.ctx) {
      this.stopAll();
      return;
    }

    const now = this.ctx.currentTime;
    const end = now + durationSeconds;

    [this.natWarmthGain, this.anthropoGain, this.aerosolGain, this.cryoGain, this.abyssalGain].forEach((g) => {
      if (g) {
        try {
          g.gain.cancelScheduledValues(now);
          g.gain.setValueAtTime(g.gain.value, now);
          g.gain.linearRampToValueAtTime(0.0001, end);
        } catch {}
      }
    });

    // Schedule cleanup after fadeout duration
    setTimeout(() => {
      this.stopAll();
    }, durationSeconds * 1000);
  }

  public stopAll() {
    this.stopNatWarmth();
    this.stopAnthropoPulse();
    this.stopAerosolShield();
    this.stopCryoHiss();
    this.stopAbyssalDrone();
  }
}
